"use client";

import {
  GetAtPath,
  Observable,
  ObservableOptions,
  PathKey,
} from "@noya-app/observable";
import { useMemo, useSyncExternalStore } from "react";

// Overloads for when observable is definitely defined
export function useObservable<T>(observable: Observable<T>): T;
export function useObservable<T, A>(
  observable: Observable<T>,
  selector: (value: T) => A,
  options?: Pick<ObservableOptions, "isEqual">
): A;
export function useObservable<T, P extends PathKey[] | string>(
  observable: Observable<T>,
  path: P
): GetAtPath<T, P>;

// Overloads for when observable might be undefined
export function useObservable<T>(
  observable: Observable<T> | undefined
): T | undefined;
export function useObservable<T, A>(
  observable: Observable<T> | undefined,
  selector: (value: T) => A,
  options?: Pick<ObservableOptions, "isEqual">
): A | undefined;
export function useObservable<T, P extends PathKey[] | string>(
  observable: Observable<T> | undefined,
  path: P
): GetAtPath<T, P> | undefined;

// Implementation
export function useObservable<T, P extends PathKey[]>(
  observable: Observable<T> | undefined,
  pathOrSelector?: P | ((value: T) => any),
  options?: Pick<ObservableOptions, "isEqual">
) {
  const { get, listen } = useMemo(() => {
    if (!observable) {
      return {
        get: () => undefined,
        listen: () => () => {},
      };
    }

    if (typeof pathOrSelector === "function") {
      const mappedObservable = observable.map(pathOrSelector, {
        isEqual: options?.isEqual,
      });
      return {
        get: mappedObservable.get,
        listen: mappedObservable.subscribe,
      };
    }

    const listen = pathOrSelector
      ? (callback: (value: GetAtPath<T, P>) => void) =>
          observable.subscribe(pathOrSelector, callback)
      : observable.subscribe;

    const get: () => void = pathOrSelector
      ? () => observable.get(pathOrSelector)
      : observable.get;

    return { get, listen };
  }, [observable, pathOrSelector, options?.isEqual]);

  return useSyncExternalStore(listen, get, get);
}
